<template>
  <Dialog
    custom-class="pg-dialog-file-preview"
    top
    fullscreen
    :show-close="false"
    :visible="showDrawer"
    append-to-body
    width="100%"
    @close="close"
    @closed="closed"
    @open="open"
  >
    <div class="pg-view-area">
      <filePreview
        class="pg-view-area_file-preview"
        v-bind="$attrs"
        ref="filePreviewRef"
        :value="filePreviewValue"
        :space.sync="isSpace"
        :transformPdfImg="transformPdfImg"
        :showPdfInIframe="showPdfInIframe"
      ></filePreview>
    </div>

    <!-- 关闭 -->
    <div class="pg-view-close pointer" @click="handleCancel">
      <i class="el-icon-close" style="font-size: 32px"></i>
    </div>

    <!-- 上一页 -->
    <div class="pg-view-btn-prev" @click="() => nextPage('prev')" v-if="multiple">
      <i
        class="el-icon-arrow-left"
        :style="{
          opacity: page.curPage === 1 ? 0.5 : 1,
          cursor: page.curPage === 1 ? 'not-allowed' : 'pointer',
          fontSize: '32px',
        }"
      ></i>
    </div>

    <!-- 下一页 -->
    <div class="pg-view-btn-next" @click="() => nextPage('next')" v-if="multiple">
      <i
        class="el-icon-arrow-right"
        :style="{
          opacity: page.curPage >= page.total ? 0.5 : 1,
          cursor: page.curPage >= page.total ? 'not-allowed' : 'pointer',
          fontSize: '32px',
        }"
      ></i>
    </div>

    <!-- Footer-tool -->
    <div class="pg-view-footer">
      <tool
        class="pg-view-footer_tool"
        :onlyDownload="false"
        :download="true"
        :adaptive="!fileIsPdf"
        :zoom="!fileIsPdf"
        :showDrag="!fileIsPdf"
        :zoomNum="getZoomNum()"
        :isSpace.sync="isSpace"
        :remove="remove"
        @zoom-origin="initSize"
        @zoomF="zoomFun"
        @zoom-adaptived="adaptived"
        @on-download="onDownload"
        @on-remove="onRemove"
      ></tool>
    </div>

    <!-- Footer -page-->
    <div class="pg-view-page" v-if="multiple">
      <span class="pg-view-page__no">{{ page.curPage }}</span>
      <span>{{ '/ ' + page.total }}</span>
    </div>
  </Dialog>
</template>
<script setup>
import { ref, computed, toRef, watch, nextTick } from 'vue'
import filePreview from './file-preview.vue'
import tool from './tool.vue'
import { isString, isObject, isArray, throttle } from 'lodash-es'
import { useDrawerPack } from '../../../hooks/index'
import { Dialog } from 'element-ui'
import { ifPdf } from '../util/file-util'

const emit = defineEmits(['on-cancle', 'update:visible'])

const props = defineProps({
  value: [Array, Object, String],
  //为 false时不显示
  visible: {
    type: Boolean,
    default: true,
  },
  // 是否多选
  multiple: {
    type: Boolean,
    default: true,
  },
  //近在multiple为true时生效，设置当前的附件
  current: [String, Object],
  transformPdfImg: {
    type: Boolean,
    default: true,
  },
  // showPdfInIframe transformPdfImg 同时为true时showPdfInIframe优先级更高
  showPdfInIframe: {
    type: Boolean,
    default: true,
  },
  remove: Boolean,
})

const open = () => {}

const { showDrawer, close, closed, handleCancel } = useDrawerPack(toRef(props, 'visible'), emit)
const filePreviewRef = ref(null)

// 分页
const page = ref({
  curPage: 1, // 当前页码
  total: props.multiple ? props.value?.length ?? 1 : 1, // 总页数
})

watch(
  [() => props.value, () => props.current],
  () => {
    if (props.multiple && props.current && props.value?.length > 0) {
      const key = props.current?.id ? 'id' : 'url' //根据传入的值判断使用id还是url进行比较
      const currentId = typeof props.current === 'string' ? props.current : props.current[key]
      page.value.curPage =
        props.value.findIndex((im) =>
          typeof im === 'string' ? im === currentId : im[key] === currentId
        ) + 1
    } else {
      page.value.curPage = 1
    }
    page.value.total = props.multiple ? props.value?.length ?? 1 : 1
  },
  {
    immediate: true,
  }
)

const filePreviewValue = computed(() => {
  if (!props.value) return null
  if (isArray(props.value)) {
    return props.value[page.value.curPage - 1]
  } else if (isObject(props.value)) {
    return props.value
  } else if (isString(props.value)) {
    return props.value
  } else {
    return null
  }
})

const fileIsPdf = computed(() => {
  const url =
    typeof filePreviewValue.value === 'string'
      ? { url: filePreviewValue.value }
      : filePreviewValue.value
  return ifPdf(url)
})

/**
 * @name 翻页-直接切换数组元素
 * @param {string} [type=next] 翻页类型 可能值: 'next' | 'prev' | 'goto'
 * @param {boolean} [isPdf=false] 是否是 pdf
 * @param {number} [curPage=1] 当前页码
 *
 */
const nextPage = throttle((type = 'next', curPage = 1) => {
  // 向后翻页
  if (type === 'next') {
    if (page.value.curPage >= page.value.total) return
    page.value.curPage += 1
  }
  // 向前翻页
  if (type === 'prev') {
    if (page.value.curPage <= 1) return
    page.value.curPage -= 1
  }
  // 指定跳转
  if (type === 'goto') {
    if (curPage < 1) return
    page.value.curPage = curPage
  }
  nextTick(() => {
    filePreviewRef.value.setZoomNum(1)
  })
}, 50)

// 初始化
const initSize = () => {
  filePreviewRef.value.initSize()
}
const zoomFun = (val) => {
  filePreviewRef.value.zoom(val, 0.1)
}

const adaptived = () => {
  filePreviewRef.value.setZoomNum(1)
  filePreviewRef.value.zoom(1, 0.1)
}

const isSpace = ref(false)
const onDownload = () => {
  filePreviewRef.value.download()
}
const onRemove = () => {
  if (!props.multiple || props.value?.length === 1) {
    handleCancel()
  }
  emit('on-remove', filePreviewValue.value)
}

const getZoomNum = () => {
  return Number(filePreviewRef.value?.getZoomNum() ?? 1) ?? 1
}
</script>
<style lang="scss">
@import '../style/file-preview-dialog.scss';
</style>
